import { DeliveryProvider, Order, SystemConfig } from '../types';

type OpenDeliveryTokenCacheEntry = {
    token: string;
    expiresAt: number;
};

type OpenDeliveryQuoteResult = {
    available: boolean;
    price: number;
    etaPickupMinutes?: number;
    etaDeliveryMinutes?: number;
    raw?: any;
};

export type CanonicalOpenDeliveryCostInput = {
    price: number;
    hasValidPrice: boolean;
};

type OpenDeliveryDeliveryResult = {
    success: boolean;
    deliveryId?: string;
    message?: string;
};

const tokenCache = new Map<string, OpenDeliveryTokenCacheEntry>();
const RETRYABLE_DELIVERY_STATUSES = new Set([429, 502, 503, 504]);
const DELIVERY_MAX_RETRIES = 2;
const DELIVERY_RETRY_BASE_MS = 500;
const IDEMPOTENCY_WINDOW_MS = 5 * 60 * 1000;

const normalizeBaseUrl = (baseUrl: string): string => baseUrl.replace(/\/$/, '');

const toSafeUuid = (raw: string): string => {
    const uuidLike = /^[0-9a-fA-F-]{36}$/;
    if (uuidLike.test(raw)) return raw;
    return crypto.randomUUID();
};

const parsePriceValue = (payload: any): number | null => {
    const fromDeliveryPrice = payload?.deliveryPrice?.price?.value;
    if (typeof fromDeliveryPrice === 'number' && Number.isFinite(fromDeliveryPrice)) return fromDeliveryPrice;

    const fromDirectPrice = payload?.price?.value;
    if (typeof fromDirectPrice === 'number' && Number.isFinite(fromDirectPrice)) return fromDirectPrice;

    return null;
};

export const buildCanonicalOpenDeliveryCostInput = (payload: any): CanonicalOpenDeliveryCostInput => {
    const parsedValue = parsePriceValue(payload);
    const hasValidPrice = parsedValue !== null && parsedValue > 0;

    return {
        price: hasValidPrice ? parsedValue : 0,
        hasValidPrice
    };
};

const toOrderCreatedAtIso = (order: Order): string => {
    if (!order.createdAt) return new Date().toISOString();
    return new Date(order.createdAt).toISOString();
};

const getVehiclePayload = () => ({
    type: ['MOTORBIKE_BAG'],
    container: 'THERMIC' as const,
    containerSize: 'MEDIUM' as const
});

const getMerchantIdentity = (config: SystemConfig, provider: DeliveryProvider) => {
    const fallbackId = provider.openDeliveryConfig?.remoteMerchantId || config.openDelivery?.merchantId || crypto.randomUUID();
    return {
        id: config.openDeliveryIdentity?.merchantId || fallbackId,
        name: config.storeName || 'Operacao SAL'
    };
};

const getPickupAddress = (config: SystemConfig) => ({
    formattedAddress: config.businessAddress || config.storeName || 'Loja',
    coordinates: {
        latitude: config.businessLat,
        longitude: config.businessLng
    },
    city: '',
    state: '',
    country: 'BR'
});

const getDeliveryAddress = (order: Order) => ({
    formattedAddress: order.address,
    streetName: order.streetAddress || order.address,
    neighborhood: order.neighborhood || '',
    city: order.city || '',
    state: order.state || '',
    country: 'BR',
    coordinates: {
        latitude: order.lat || 0,
        longitude: order.lng || 0
    }
});

const toPaymentPayload = (order: Order) => {
    const normalized = (order.paymentMethod || '').toLowerCase();
    const isOnline = normalized.includes('online') || normalized.includes('pix');
    return [
        {
            amount: {
                value: order.orderValue,
                currency: 'BRL'
            },
            method: isOnline ? 'ONLINE' : 'CASH',
            prepaid: isOnline
        }
    ];
};

const getTokenCacheKey = (baseUrl: string, clientId: string): string => `${baseUrl}::${clientId}`;

const wait = async (ms: number): Promise<void> => {
    await new Promise((resolve) => setTimeout(resolve, ms));
};

const buildDeliveryIdempotencyKey = (order: Order, provider: DeliveryProvider): string => {
    const windowBucket = Math.floor(Date.now() / IDEMPOTENCY_WINDOW_MS);
    const providerKey = provider.id || provider.name || 'provider';
    return `${order.id}:${providerKey}:${windowBucket}`;
};

const fetchDeliveryWithRetry = async (url: string, init: RequestInit): Promise<Response> => {
    let lastNetworkError: Error | null = null;

    for (let attempt = 0; attempt <= DELIVERY_MAX_RETRIES; attempt += 1) {
        try {
            const response = await fetch(url, init);
            if (!RETRYABLE_DELIVERY_STATUSES.has(response.status) || attempt === DELIVERY_MAX_RETRIES) {
                return response;
            }
        } catch (error) {
            lastNetworkError = error instanceof Error ? error : new Error('Falha de rede no envio externo');
            if (attempt === DELIVERY_MAX_RETRIES) {
                throw lastNetworkError;
            }
        }

        const backoffMs = DELIVERY_RETRY_BASE_MS * (2 ** attempt);
        await wait(backoffMs);
    }

    if (lastNetworkError) throw lastNetworkError;
    throw new Error('Falha ao enviar para Open Delivery');
};

const requestOpenDeliveryToken = async (
    baseUrl: string,
    clientId: string,
    clientSecret: string,
    fallbackIdentity?: { merchantId: string; merchantSecret: string }
): Promise<string> => {
    const effectiveClientId = clientId || fallbackIdentity?.merchantId || '';
    const effectiveClientSecret = clientSecret || fallbackIdentity?.merchantSecret || '';

    if (!effectiveClientId || !effectiveClientSecret) {
        throw new Error('Credenciais de autenticação ausentes (Client ID/Secret ou Merchant Identity)');
    }

    const cacheKey = getTokenCacheKey(baseUrl, effectiveClientId);
    const cached = tokenCache.get(cacheKey);
    const now = Date.now();

    if (cached && cached.expiresAt > now + 30_000) {
        return cached.token;
    }

    const body = new URLSearchParams({
        grant_type: 'client_credentials',
        client_id: effectiveClientId,
        client_secret: effectiveClientSecret
    });

    const response = await fetch(`${baseUrl}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body
    });

    if (!response.ok) {
        throw new Error(`OAuth token error (${response.status})`);
    }

    const tokenPayload = await response.json();
    const token = tokenPayload?.access_token;
    const expiresInSeconds = Number(tokenPayload?.expires_in || 300);

    if (!token || typeof token !== 'string') {
        throw new Error('OAuth token missing in response');
    }

    tokenCache.set(cacheKey, {
        token,
        expiresAt: now + (expiresInSeconds * 1000)
    });

    return token;
};

export const getOpenDeliveryAvailabilityQuote = async ({
    order,
    provider,
    config,
    origin,
    distanceKm
}: {
    order: Order;
    provider: DeliveryProvider;
    config: SystemConfig;
    origin?: { lat: number; lng: number };
    distanceKm?: number;
}): Promise<OpenDeliveryQuoteResult> => {
    const odConfig = provider.openDeliveryConfig;
    if (!odConfig?.baseUrl) {
        return { available: false, price: 0 };
    }

    // Verifica se tem credenciais diretas ou identity global
    const hasDirectCreds = odConfig.clientId && odConfig.clientSecret;
    const hasGlobalIdentity = config.openDeliveryIdentity?.merchantId && config.openDeliveryIdentity?.merchantSecret;
    
    if (!hasDirectCreds && !hasGlobalIdentity) {
        return { available: false, price: 0 };
    }

    if (typeof order.lat !== 'number' || typeof order.lng !== 'number') {
        return { available: false, price: 0 };
    }

    const baseUrl = normalizeBaseUrl(odConfig.baseUrl);
    const fallbackIdentity = config.openDeliveryIdentity;
    const token = await requestOpenDeliveryToken(
        baseUrl, 
        odConfig.clientId || '', 
        odConfig.clientSecret || '',
        fallbackIdentity
    );
    const merchant = getMerchantIdentity(config, provider);
    const pickupLat = origin?.lat ?? config.businessLat;
    const pickupLng = origin?.lng ?? config.businessLng;

    const payload = {
        merchant,
        pickupAddress: {
            ...getPickupAddress(config),
            coordinates: { latitude: pickupLat, longitude: pickupLng }
        },
        returnToMerchant: false,
        canCombine: true,
        deliveryAddress: getDeliveryAddress(order),
        vehicle: getVehiclePayload(),
        limitTimes: {
            pickupLimit: Math.max(15, Math.floor((config.averageKitchenTime || 10) + 5)),
            deliveryLimit: Math.max(order.timeLimit || 45, 20),
            orderCreatedAt: toOrderCreatedAtIso(order)
        },
        totalOrderPrice: { value: order.orderValue, currency: 'BRL' },
        orderDeliveryFee: { value: order.deliveryFee || 0, currency: 'BRL' },
        totalWeight: Math.max(500, Math.round((distanceKm || order.distance || 1) * 250))
    };

    const response = await fetch(`${baseUrl}/v1/logistics/availability`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        return { available: false, price: 0 };
    }

    const data = await response.json();
    const canonicalCost = buildCanonicalOpenDeliveryCostInput(data);

    if (!canonicalCost.hasValidPrice) {
        return { available: false, price: 0, raw: data };
    }

    return {
        available: true,
        price: canonicalCost.price,
        etaPickupMinutes: data?.ETAs?.pickupEtaInMinutes,
        etaDeliveryMinutes: data?.ETAs?.deliveryEtaInMinutes,
        raw: data
    };
};

// Função para modo "inverted" - SAL chama webhook do parceiro (ex: Taxi Machine)
const requestDeliveryViaPartnerWebhook = async ({
    order,
    provider,
    config,
    notifyPickup = true,
    notifyConclusion = true
}: {
    order: Order;
    provider: DeliveryProvider;
    config: SystemConfig;
    notifyPickup?: boolean;
    notifyConclusion?: boolean;
}): Promise<OpenDeliveryDeliveryResult> => {
    const odConfig = provider.openDeliveryConfig;
    const partnerWebhookUrl = odConfig?.partnerWebhookUrl || odConfig?.baseUrl;
    
    if (!partnerWebhookUrl) {
        return { success: false, message: 'Webhook URL do parceiro não configurada' };
    }

    if (typeof order.lat !== 'number' || typeof order.lng !== 'number') {
        return { success: false, message: 'Pedido sem coordenadas para despacho externo' };
    }

    const merchant = getMerchantIdentity(config, provider);
    
    // Payload simplificado para enviar ao webhook do parceiro
    const payload = {
        orderId: toSafeUuid(order.id),
        orderDisplayId: order.displayId || order.id,
        merchantId: odConfig?.remoteMerchantId || merchant.id,
        merchantName: merchant.name,
        pickupAddress: getPickupAddress(config),
        deliveryAddress: getDeliveryAddress(order),
        customerName: order.customerName,
        customerPhone: order.phone || '',
        orderValue: order.orderValue,
        deliveryFee: order.deliveryFee || 0,
        notifyPickup,
        notifyConclusion,
        // Incluir credenciais de autenticação se disponíveis
        credentials: config.openDeliveryIdentity ? {
            merchantId: config.openDeliveryIdentity.merchantId,
            merchantSecret: config.openDeliveryIdentity.merchantSecret
        } : undefined
    };

    try {
        const response = await fetchDeliveryWithRetry(partnerWebhookUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-App-Id': 'sal-rota',
                'X-App-MerchantId': merchant.id
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const body = await response.text();
            return {
                success: false,
                message: `Erro ${response.status} ao chamar webhook do parceiro (${body.slice(0, 180)})`
            };
        }

        let parsed: any = null;
        try {
            parsed = await response.json();
        } catch {
            parsed = null;
        }

        const deliveryId = parsed?.deliveryId || parsed?.id || parsed?.trackingId;
        return { success: true, deliveryId };
    } catch (error) {
        const message = error instanceof Error ? error.message : 'erro de rede';
        return { success: false, message: `Falha de rede ao chamar webhook (${message})` };
    }
};

export const requestOpenDeliveryDelivery = async ({
    order,
    provider,
    config,
    notifyPickup = true,
    notifyConclusion = true
}: {
    order: Order;
    provider: DeliveryProvider;
    config: SystemConfig;
    notifyPickup?: boolean;
    notifyConclusion?: boolean;
}): Promise<OpenDeliveryDeliveryResult> => {
    const odConfig = provider.openDeliveryConfig;
    
    if (!odConfig?.baseUrl) {
        return { success: false, message: 'Base URL do parceiro Open Delivery não configurada' };
    }

    // Verifica o modo de webhook
    const isInvertedMode = odConfig?.webhookMode === 'inverted';
    
    if (isInvertedMode) {
        // Modo invertido: SAL chama webhook do parceiro
        return requestDeliveryViaPartnerWebhook({
            order,
            provider,
            config,
            notifyPickup,
            notifyConclusion
        });
    }

    // Modo padrão Open Delivery: SAL chama API do parceiro
    const hasDirectCreds = odConfig.clientId && odConfig.clientSecret;
    const hasGlobalIdentity = config.openDeliveryIdentity?.merchantId && config.openDeliveryIdentity?.merchantSecret;
    
    if (!hasDirectCreds && !hasGlobalIdentity) {
        return { success: false, message: 'Credenciais Open Delivery ausentes. Configure Client ID/Secret ou gere a Identidade Global.' };
    }

    if (typeof order.lat !== 'number' || typeof order.lng !== 'number') {
        return { success: false, message: 'Pedido sem coordenadas para despacho externo' };
    }

    const baseUrl = normalizeBaseUrl(odConfig.baseUrl);
    const fallbackIdentity = config.openDeliveryIdentity;
    const token = await requestOpenDeliveryToken(
        baseUrl, 
        odConfig.clientId || '', 
        odConfig.clientSecret || '',
        fallbackIdentity
    );
    const merchant = getMerchantIdentity(config, provider);
    const idempotencyKey = buildDeliveryIdempotencyKey(order, provider);

    const payload = {
        orderId: toSafeUuid(order.id),
        orderDisplayId: order.displayId || order.id,
        merchant,
        pickupAddress: getPickupAddress(config),
        pickupCode: order.pickupCode,
        notifyPickup,
        notifyConclusion,
        returnToMerchant: false,
        canCombine: true,
        deliveryAddress: getDeliveryAddress(order),
        customerName: order.customerName,
        customerPhone: order.phone || '',
        vehicle: getVehiclePayload(),
        limitTimes: {
            pickupLimit: Math.max(15, Math.floor((config.averageKitchenTime || 10) + 5)),
            deliveryLimit: Math.max(order.timeLimit || 45, 20),
            orderCreatedAt: toOrderCreatedAtIso(order)
        },
        totalOrderPrice: { value: order.orderValue, currency: 'BRL' },
        orderDeliveryFee: { value: order.deliveryFee || 0, currency: 'BRL' },
        totalWeight: Math.max(500, Math.round((order.distance || 1) * 250)),
        payments: toPaymentPayload(order)
    };

    let response: Response;
    try {
        response = await fetchDeliveryWithRetry(`${baseUrl}/v1/logistics/delivery`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'Idempotency-Key': idempotencyKey
            },
            body: JSON.stringify(payload)
        });
    } catch (error) {
        const message = error instanceof Error ? error.message : 'erro de rede';
        return { success: false, message: `Falha de rede ao despachar (${message})` };
    }

    if (!response.ok) {
        const body = await response.text();
        return {
            success: false,
            message: `Erro ${response.status} ao despachar (${body.slice(0, 180)})`
        };
    }

    let parsed: any = null;
    try {
        parsed = await response.json();
    } catch {
        parsed = null;
    }

    const deliveryId = parsed?.deliveryId || parsed?.id;
    return { success: true, deliveryId };
};
